


import React, { useCallback, useEffect, useState } from "react";
import {
  Col,
  Row,
  Space,
  Input,
  Table,
  Switch,
  Button,
  Modal,
  Form,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  DeploymentUnitOutlined,
  PlusCircleOutlined,
  RocketOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { API_BASE_URL } from "../config/appUrl";

import MyTaskFilterDate from "../components/MyTaskFilter";
import constants from "../utils/constants";
import "../scss/botListing.scss";
import CardItem from "../components/cardItem/CardItem";
import AddNewBot from "../utils/AddNewBot";
import AddNewUser from "../components/AddNewUser";
import axios from "axios";
import appUrl from "../config/appUrl";
import { showNotification } from "../utils/commonFunctions";
import { useNavigate } from "react-router-dom";
import routes from "../routes_mapper";
import { getRole } from "../utils/commonFunctions"; // util you mentioned

const { FILTER_TYPE_DATE } = constants;
const defaultFilterType = FILTER_TYPE_DATE?.All?._id;

const ROLES = {
  ADMIN: "admin",
  TENANT: "tenant",
  BOT: "bot",
};

const BotListing = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [botForm] = Form.useForm();
  const [filteredData, setFilteredData] = useState([]);
  const [showInviteUser, setShowInviteUser] = useState(false);
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [showEditTenant, setShowEditTenant] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [showEditBot, setShowEditBot] = useState(false);
  const [editingBot, setEditingBot] = useState(null);
  const [editBotLoading, setEditBotLoading] = useState(false);
  const [states, setStates] = useState({
    search: "",
    ids: [],
    filterType: defaultFilterType,
    modelOpen: false,
    data: [],
    start_date: "",
    end_date: "",
  });
  const [dashboardData, setDashboardData] = useState({});
  const [loadingIds, setLoadingIds] = useState([]); // ✅ Track loading switches
  const [deletingIds, setDeletingIds] = useState([]); // ✅ Track deleting tenants

  const currentRole = getRole(); // returns "admin" | "tenant" | "bot"

  useEffect(() => {
    dashboardApiCall();
  }, []);

  const transformDataForTable = (apiData) => {
    if (currentRole === ROLES.ADMIN) {
      const allRows = [];
      apiData.tenant_stats?.forEach((tenant) => {
        allRows.push({
          _id: tenant.tenant_id,
          tenant_name: tenant.tenant_name,
          tenant_id: tenant.tenant_id,
          name: tenant.tenant_name,
          email: tenant.tenant_email || tenant.email,
          active: tenant.tenant_status === "active",
          bot_id: "-",
          type: "tenant",
          queries_answered: tenant.total_queries || 0,
          token_usage: tenant.total_tokens || 0,
        });

        tenant.bot_stats?.forEach((bot) => {
          allRows.push({
            ...bot,
            tenant_name: tenant.tenant_name,
            tenant_id: tenant.tenant_id,
            name: tenant.tenant_name,
            email: bot.bot_email,
            active: bot.bot_status === "active",
            _id: bot.bot_id,
            bot_id: bot.bot_id,
            type: "bot",
          });
        });
      });
      return allRows;
    } else if (currentRole === ROLES.TENANT) {
      return (
        apiData.bot_stats?.map((bot) => ({
          ...bot,
          name: bot.bot_name,
          email: bot.bot_email,
          active: bot.bot_status === "active",
          _id: bot.bot_id,
          bot_id: bot.bot_id,
          tenant_id: apiData.tenant_id || localStorage.getItem("tenant_id"),
          type: "bot",
        })) || []
      );
    } else if (currentRole === ROLES.BOT) {
      return [
        {
          ...apiData,
          bot_name: apiData.bot_name,
          name: apiData.bot_name,
          email: apiData.bot_email,
          active: apiData.status === "active",
          _id: apiData.bot_id,
          bot_id: apiData.bot_id,
          tenant_id: apiData.tenant_id,
          llm_model: apiData.llm_model,
          queries_answered: apiData.queries_answered,
          token_usage: apiData.token_usage,
          total_cost: apiData.total_cost,
          type: "bot",
        },
      ];
    }
    return [];
  };

  const dashboardApiCall = () => {
    axios
      .get(`${appUrl.DASHBOARD}?email=${localStorage.getItem("email")}`)
      .then((response) => {
        const apiData = response?.data || {};
        setDashboardData(apiData);
        const transformedData = transformDataForTable(apiData);
        setFilteredData(transformedData);
      })
      .catch((err) => {
        showNotification("error", err.message || "Failed to fetch dashboard");
      });
  };

  const handleClickList = useCallback((id) => {
    setStates((prevState) => ({
      ...prevState,
      filterType: id,
      modelOpen: !prevState.modelOpen,
    }));
  }, []);

  const handleChange = useCallback(
    (name) => (event) => {
      let value = event?.target?.value ?? event;
      setStates((prevState) => ({ ...prevState, [name]: value }));
    },
    []
  );

  const handleInviteUserModal = useCallback((show = false) => {
    show = typeof show === "boolean" && show;
    setShowInviteUser(show);
  }, []);

  const handleAddTenantModal = useCallback((show = false) => {
    show = typeof show === "boolean" && show;
    setShowAddTenant(show);
  }, []);

  // ✅ Handle Edit Tenant Modal
  const handleEditTenantModal = useCallback((show = false, tenant = null) => {
    show = typeof show === "boolean" && show;
    setShowEditTenant(show);
    if (show && tenant) {
      setEditingTenant(tenant);
      form.setFieldsValue({
        organization_name: tenant.tenant_name,
        email: tenant.tenant_email || tenant.email,
      });
    } else {
      setEditingTenant(null);
      form.resetFields();
    }
  }, [form]);

  // ✅ Handle Edit Tenant Submit
  const handleEditTenantSubmit = useCallback(async (values) => {
    if (!editingTenant) return;

    setEditLoading(true);
    try {
      const response = await axios.put(
        `${appUrl.TENANT}?tenant_id=${editingTenant.tenant_id}`,
        values,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success(response.data?.message || "Tenant updated successfully");
      setShowEditTenant(false);
      setEditingTenant(null);
      form.resetFields();
      dashboardApiCall();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to update tenant";
      message.error(errorMessage);
    } finally {
      setEditLoading(false);
    }
  }, [editingTenant, form]);

  // ✅ Handle Edit Bot Modal
  const handleEditBotModal = useCallback((show = false, bot = null) => {
    show = typeof show === "boolean" && show;
    setShowEditBot(show);
    if (show && bot) {
      setEditingBot(bot);
      botForm.setFieldsValue({
        bot_name: bot.bot_name,
        email: bot.bot_email,
      });
    } else {
      setEditingBot(null);
      botForm.resetFields();
    }
  }, [botForm]);

  // ✅ Handle Edit Bot Submit
  const handleEditBotSubmit = useCallback(async (values) => {
    if (!editingBot) return;

    setEditBotLoading(true);
    try {
      let url = `${appUrl.UPDATE_BOT}?bot_id=${editingBot.bot_id}`;
      
      // For admin, include tenant_id in query
      if (currentRole === ROLES.ADMIN && editingBot.tenant_id) {
        url = `${appUrl.UPDATE_BOT}?bot_id=${editingBot.bot_id}&tenant_id=${editingBot.tenant_id}`;
      }

      const response = await axios.put(url, values, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
      });

      message.success(response.data?.message || "Bot updated successfully");
      setShowEditBot(false);
      setEditingBot(null);
      botForm.resetFields();
      dashboardApiCall();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to update bot";
      message.error(errorMessage);
    } finally {
      setEditBotLoading(false);
    }
  }, [editingBot, botForm, currentRole]);

  const handleUserInvite = useCallback((payload) => {
    return axios
      .post(appUrl.TENANT, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setShowInviteUser(false);
        dashboardApiCall();
        return response;
      })
      .catch((err) => {
        const errorMessage =
          err.response?.data?.message || err.message || "Failed to invite user";
        showNotification("error", errorMessage);
        throw err.response?.data || err;
      });
  }, []);

  const handleTenantAdd = useCallback((payload, query) => {
    const url = query
      ? `${API_BASE_URL}/create-bot${query}`
      : `${API_BASE_URL}/create-bot`;

    return axios
      .post(url, payload)
      .then((response) => {
        setShowAddTenant(false);
        dashboardApiCall();
        return response;
      })
      .catch((err) => {
        const errorMessage =
          err.response?.data?.message || err.message || "Failed to add tenant";
        showNotification("error", errorMessage);
        throw err.response?.data || err;
      });
  }, []);

  // ✅ Toggle Bot or Tenant Status
  const handleUserUpdate = useCallback(
    (record) => {
      let url = "";
      let loadingKey =
        record.type === "tenant" ? record.tenant_id : record.bot_id;

      if (record.type === "bot") {
        url = `${appUrl.TOGGLE_BOT_STATUS}?bot_id=${record.bot_id}`;
        if (currentRole === ROLES.ADMIN && record.tenant_id) {
          url = `${appUrl.TOGGLE_BOT_STATUS}?tenant_id=${record.tenant_id}&bot_id=${record.bot_id}`;
        }
      } else if (record.type === "tenant" && currentRole === ROLES.ADMIN) {
        url = `${appUrl.TOGGLE_TENANT_STATUS}?tenant_id=${record.tenant_id}`;
      } else {
        return;
      }

      setLoadingIds((prev) => [...prev, loadingKey]);
      axios
        .put(
          url,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        )
        .then((res) => {
          showNotification(
            "success",
            res.data?.message || "Status updated successfully"
          );
          dashboardApiCall();
        })
        .catch((err) => {
          const errorMessage =
            err.response?.data?.message ||
            err.message ||
            "Failed to toggle status";
          showNotification("error", errorMessage);
        })
        .finally(() => {
          setLoadingIds((prev) => prev.filter((id) => id !== loadingKey));
        });
    },
    [currentRole]
  );

  // ✅ Delete Tenant with Confirmation
  const handleTenantDelete = useCallback((record) => {
    console.log("Delete clicked for record:", record); // Debug log
    console.log("Current role:", currentRole); // Debug log
    
    if (currentRole !== ROLES.ADMIN) {
      console.log("Not admin, cannot delete");
      return;
    }

    Modal.confirm({
      title: "Delete Tenant",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete tenant "${record.tenant_name}"? This action cannot be undone.`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        const tenantId = record.tenant_id;
        console.log("Deleting tenant with ID:", tenantId); // Debug log
        setDeletingIds((prev) => [...prev, tenantId]);

        axios
          .delete(`${appUrl.TENANT}?tenant_id=${tenantId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          })
          .then((res) => {
            console.log("Delete response:", res); // Debug log
            showNotification(
              "success",
              res.data?.message || "Tenant deleted successfully"
            );
            dashboardApiCall();
          })
          .catch((err) => {
            console.error("Delete error:", err); // Debug log
            const errorMessage =
              err.response?.data?.message ||
              err.message ||
              "Failed to delete tenant";
            showNotification("error", errorMessage);
          })
          .finally(() => {
            setDeletingIds((prev) => prev.filter((id) => id !== tenantId));
          });
      },
      onCancel: () => {
        console.log("Delete cancelled"); // Debug log
      },
    });
  }, [currentRole]);

  // ✅ Delete Bot with Confirmation
  const handleBotDelete = useCallback((record) => {
    console.log("Bot delete clicked for record:", record); // Debug log
    console.log("Current role:", currentRole); // Debug log
    
    if (currentRole === ROLES.BOT) {
      console.log("Bot role cannot delete");
      return;
    }

    Modal.confirm({
      title: "Delete Bot",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete bot "${record.bot_name}"? This action cannot be undone.`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        const botId = record.bot_id;
        console.log("Deleting bot with ID:", botId); // Debug log
        setDeletingIds((prev) => [...prev, botId]);

        // Construct URL based on role
        let url = `${appUrl.DELETE_BOT}?bot_id=${botId}`;
        if (currentRole === ROLES.ADMIN && record.tenant_id) {
          url = `${appUrl.DELETE_BOT}?tenant_id=${record.tenant_id}&bot_id=${botId}`;
        }

        axios
          .delete(url, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          })
          .then((res) => {
            console.log("Bot delete response:", res); // Debug log
            showNotification(
              "success",
              res.data?.message || "Bot deleted successfully"
            );
            dashboardApiCall();
          })
          .catch((err) => {
            console.error("Bot delete error:", err); // Debug log
            const errorMessage =
              err.response?.data?.message ||
              err.message ||
              "Failed to delete bot";
            showNotification("error", errorMessage);
          })
          .finally(() => {
            setDeletingIds((prev) => prev.filter((id) => id !== botId));
          });
      },
      onCancel: () => {
        console.log("Bot delete cancelled"); // Debug log
      },
    });
  }, [currentRole]);

  const handleUserDelete = useCallback((record) => {
    console.log("handleUserDelete called with:", record); // Debug log
    
    if (currentRole === ROLES.ADMIN) {
      // Check if this is from the main tenant table (not expanded bot rows)
      if (record.tenant_id && !record.bot_id) {
        handleTenantDelete({ ...record, type: "tenant" });
      } else if (record.bot_id) {
        // This is a bot row
        handleBotDelete({ ...record, type: "bot" });
      }
    } else if (currentRole === ROLES.TENANT) {
      // Tenant can only delete bots
      if (record.bot_id) {
        handleBotDelete({ ...record, type: "bot" });
      }
    }
  }, [currentRole, handleTenantDelete, handleBotDelete]);

  // ✅ Tenant table for admin
  const adminTenantColumns = () => [
    {
      title: "S.NO.",
      dataIndex: "key",
      width: 100,
      key: "key",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Organization",
      dataIndex: "tenant_name",
      key: "tenant_name",
    },
    {
      title: "Tenant ID",
      dataIndex: "tenant_id",
      key: "tenant_id",
    },
    {
      title: "Email Address",
      dataIndex: "tenant_email",
      key: "tenant_email",
    },
    {
      title: "Total Bots",
      dataIndex: "active_bots",
      key: "active_bots",
      render: (_, record) =>
        Number(record?.active_bots) + Number(record?.inactive_bots),
    },
    {
      title: "Status",
      dataIndex: "tenant_status",
      key: "tenant_status",
      render: (_, record) => (
        <Switch
          disabled={currentRole === ROLES.BOT}
          loading={loadingIds.includes(record.tenant_id)}
          onChange={() => handleUserUpdate({ ...record, type: "tenant" })}
          checked={record.tenant_status === "active"}
          checkedChildren={<CheckOutlined style={{ color: "#fff" }} />}
          unCheckedChildren={<CloseOutlined />}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <EditOutlined
            onClick={() => handleEditTenantModal(true, record)}
            style={{ 
              fontSize: "18px", 
              color: "#1890ff", 
              cursor: "pointer"
            }}
            title="Edit Tenant"
          />
          <DeleteOutlined
            onClick={() => handleUserDelete(record)}
            style={{ 
              fontSize: "18px", 
              color: deletingIds.includes(record.tenant_id) ? "#ccc" : "#ff4d4f", 
              cursor: deletingIds.includes(record.tenant_id) ? "not-allowed" : "pointer",
              opacity: deletingIds.includes(record.tenant_id) ? 0.6 : 1
            }}
            spin={deletingIds.includes(record.tenant_id)}
            title="Delete Tenant"
          />
        </Space>
      ),
    },
  ];

  // ✅ Bot table for admin
  const adminBotColumns = () => [
    {
      title: "Bot Name",
      dataIndex: "bot_name",
      key: "bot_name",
    },
    {
      title: "Email Address",
      dataIndex: "bot_email",
      key: "bot_email",
    },
    {
      title: "Bot ID",
      dataIndex: "bot_id",
      key: "bot_id",
    },
    {
      title: "LLM Model",
      dataIndex: "llm_model",
      key: "llm_model",
    },
    {
      title: "Token Usage",
      dataIndex: "token_usage",
      key: "token_usage",
    },
    {
      title: "Queries Answered",
      dataIndex: "queries_answered",
      key: "queries_answered",
    },
    {
      title: "Launch",
      key: "launch",
      render: (_, record) => (
        <RocketOutlined
          onClick={() => {
            const queryParams = new URLSearchParams();
            queryParams.append(
              "tenant_id",
              record.tenant_id || localStorage.getItem("tenant_id") || ""
            );
            queryParams.append("bot_id", record.bot_id || "");
            navigate(`${routes.LaunchBot}?${queryParams.toString()}`, {
              state: { ...record },
            });
          }}
          style={{ fontSize: "20px", color: "#2a2a2a", cursor: "pointer" }}
        />
      ),
    },
    {
      title: "Train",
      key: "train",
      render: (_, record) => (
        <DeploymentUnitOutlined
          onClick={() => {
            const queryParams = new URLSearchParams();
            queryParams.append(
              "tenant_id",
              record.tenant_id || localStorage.getItem("tenant_id") || ""
            );
            queryParams.append("bot_id", record.bot_id || "");
            navigate(`${routes.TrainingBot}?${queryParams.toString()}`, {
              state: { ...record },
            });
          }}
          style={{ fontSize: "20px", color: "#2a2a2a", cursor: "pointer" }}
        />
      ),
    },
    {
      title: "Query Log",
      key: "query_log",
      render: (_, record) => (
        <QuestionCircleOutlined
          onClick={() => {
            const queryParams = new URLSearchParams();
            queryParams.append(
              "tenant_id",
              record.tenant_id || localStorage.getItem("tenant_id") || ""
            );
            queryParams.append("bot_id", record.bot_id || "");
            navigate(`${routes.QUERYDETAILS}?${queryParams.toString()}`, {
              state: { ...record },
            });
          }}
          style={{ fontSize: "18px", color: "#2a2a2a", cursor: "pointer" }}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "bot_status",
      key: "bot_status",
      render: (_, record) => (
        <Switch
          disabled={currentRole === ROLES.BOT}
          loading={loadingIds.includes(record.bot_id)}
          onChange={() => handleUserUpdate({ ...record, type: "bot" })}
          checked={record.bot_status === "active" || record.status === "active"} 
          checkedChildren={<CheckOutlined style={{ color: "#fff" }} />}
          unCheckedChildren={<CloseOutlined />}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          {(currentRole === ROLES.ADMIN || currentRole === ROLES.TENANT) && (
            <EditOutlined
              onClick={() => handleEditBotModal(true, record)}
              style={{ 
                fontSize: "18px", 
                color: "#1890ff", 
                cursor: "pointer"
              }}
              title="Edit Bot"
            />
          )}
          <DeleteOutlined
            onClick={() => handleUserDelete(record)}
            style={{ 
              fontSize: "18px", 
              color: deletingIds.includes(record.bot_id) ? "#ccc" : "#ff4d4f", 
              cursor: deletingIds.includes(record.bot_id) ? "not-allowed" : "pointer",
              opacity: deletingIds.includes(record.bot_id) ? 0.6 : 1
            }}
            spin={deletingIds.includes(record.bot_id)}
            title="Delete Bot"
          />
        </Space>
      ),
    },
  ];

  const shouldShowAddTenantButton = () => currentRole === ROLES.ADMIN;
  const shouldShowAddBotButton = () =>
    currentRole === ROLES.ADMIN || currentRole === ROLES.TENANT;
  const shouldShowSearchAndFilter = () => currentRole !== ROLES.BOT;

  return (
    <Row justify="space-between" className="listing-cb">
      <div style={{ width: "100%", paddingBottom: "50px" }}>
        <CardItem dashboardData={dashboardData} />

        {shouldShowSearchAndFilter() && (
          <Row
            className="mb20"
            justify="space-between"
            style={{ width: "100%", marginTop: "30px" }}
          >
            <Col xs={24} sm={24} md={12}>
              <Space align="center">
                <Input
                  id="searchInput"
                  placeholder="Search..."
                  prefix={<SearchOutlined />}
                  size="large"
                />
                <MyTaskFilterDate
                  handleChange={handleChange}
                  state={states}
                  handleClickList={handleClickList}
                />
                <Button type="primary" size="large">
                  Submit
                </Button>
                <Button size="large" className="reset-btn">
                  Reset
                </Button>
              </Space>
            </Col>
            <Col xs={24} md={12} align="end">
              <Space>
                {shouldShowAddTenantButton() && (
                  <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    size="large"
                    onClick={() => handleInviteUserModal(true)}
                  >
                    Add New Tenant
                  </Button>
                )}
                {shouldShowAddBotButton() && (
                  <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    size="large"
                    onClick={() => handleAddTenantModal(true)}
                  >
                    Add New Bot
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        )}

        <Col span={24}>
          {currentRole !== ROLES.ADMIN && (
            <Table
              columns={adminBotColumns()}
              dataSource={filteredData}
              pagination={false}
              bordered
              rowKey="_id"
            />
          )}

          {currentRole === ROLES.ADMIN && (
            <Table
              columns={adminTenantColumns()}
              dataSource={dashboardData ? dashboardData.tenant_stats : []}
              pagination={false}
              rowKey={"tenant_id"}
              expandable={{
                expandedRowRender: (record) => (
                  <Table
                    columns={adminBotColumns()}
                    dataSource={record?.bot_stats || []}
                    pagination={false}
                    bordered
                    size="small"
                    rowKey="tenant_id"
                  />
                ),
              }}
            />
          )}
        </Col>

        {/* ✅ Edit Bot Modal */}
        <Modal
          title="Edit Bot"
          open={showEditBot}
          onCancel={() => handleEditBotModal(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            form={botForm}
            layout="vertical"
            onFinish={handleEditBotSubmit}
            disabled={editBotLoading}
          >
            <Form.Item
              label="Bot Name"
              name="bot_name"
              rules={[
                { required: true, message: "Please enter bot name" },
                { min: 2, message: "Bot name must be at least 2 characters" }
              ]}
            >
              <Input placeholder="Enter bot name" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter email address" },
                { type: "email", message: "Please enter a valid email address" }
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button 
                  onClick={() => handleEditBotModal(false)}
                  disabled={editBotLoading}
                >
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={editBotLoading}
                >
                  Update Bot
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* ✅ Edit Tenant Modal */}
        <Modal
          title="Edit Tenant"
          open={showEditTenant}
          onCancel={() => handleEditTenantModal(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleEditTenantSubmit}
            disabled={editLoading}
          >
            <Form.Item
              label="Organization Name"
              name="organization_name"
              rules={[
                { required: true, message: "Please enter organization name" },
                { min: 2, message: "Organization name must be at least 2 characters" }
              ]}
            >
              <Input placeholder="Enter organization name" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter email address" },
                { type: "email", message: "Please enter a valid email address" }
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button 
                  onClick={() => handleEditTenantModal(false)}
                  disabled={editLoading}
                >
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={editLoading}
                >
                  Update Tenant
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <AddNewBot
          visible={showInviteUser}
          handleModal={handleInviteUserModal}
          onUserInvite={handleUserInvite}
        />

        <AddNewUser
          visible={showAddTenant}
          handleModal={handleAddTenantModal}
          onUserInvite={handleTenantAdd}
        />
      </div>
    </Row>
  );
};

export default BotListing;
